import { type Vec2 } from '@/domain/math';

import { type Camera, panByScreen, zoomAt, type Viewport } from './Camera';

/**
 * Viewport navigation: pan and zoom.
 *
 * Deliberately separate from the tool system that arrives in M7. Navigation is not
 * editing — it changes nothing in the document, produces no undoable command, and must
 * keep working whichever tool is active. Modelling it as "just another tool" is a
 * recurring mistake that makes middle-drag panning conflict with the active tool.
 *
 * Frame-rate independent by construction: every method is a pure function of the current
 * camera and the gesture, with no time-based easing, so behaviour is identical whether
 * events arrive at 60 Hz or 240 Hz.
 */

export interface PanState {
  readonly lastScreen: Vec2;
}

/** Wheel-to-zoom sensitivity. Tuned so one notch on a typical mouse is ~1.1x. */
const WHEEL_ZOOM_BASE = 1.0015;

/** Ctrl/⌘-wheel (pinch on a trackpad) reports much larger deltas; damp them. */
const PINCH_DAMPING = 0.35;

export function beginPan(screen: Vec2): PanState {
  return { lastScreen: screen };
}

export function continuePan(
  state: PanState,
  camera: Camera,
  screen: Vec2
): { readonly camera: Camera; readonly state: PanState } {
  const dx = screen.x - state.lastScreen.x;
  const dy = screen.y - state.lastScreen.y;
  return {
    camera: panByScreen(camera, dx, dy),
    state: { lastScreen: screen },
  };
}

/**
 * What a wheel or trackpad gesture asked for, with no DOM types in it.
 *
 * `zoom` is Ctrl or ⌘ — and it is also what a trackpad pinch reports, which is why the two
 * arrive as one flag rather than two.
 */
export interface WheelGesture {
  readonly deltaX: number;
  readonly deltaY: number;
  readonly zoom: boolean;
  readonly shift: boolean;
}

/**
 * Applies a wheel or trackpad gesture: scrolling moves the drawing, Ctrl or a pinch zooms it.
 *
 * ## Why scrolling does not zoom
 *
 * It used to, and on a laptop that is wrong in the first second of use: two fingers on a
 * trackpad is the gesture for *moving about*, and a canvas that magnifies instead feels like it
 * has misheard. Every editor a teacher already knows agrees — Figma, n8n, a map, a PDF reader,
 * the browser itself — scroll to move, Ctrl to zoom. Being the one program that does otherwise
 * costs more than the shortcut saves.
 *
 * A mouse wheel still zooms, with Ctrl held, which is the same chord those programs use.
 *
 * ## Why the zoom is exponential
 *
 * `deltaY` is exponentiated rather than added, because zoom is multiplicative: two notches must
 * compose to the same factor whatever the current level. A linear `zoom += delta` feels sluggish
 * when zoomed in and violently fast when zoomed out, which is the commonest feel bug in canvas
 * editors.
 */
export function applyWheel(
  camera: Camera,
  viewport: Viewport,
  anchorScreen: Vec2,
  gesture: WheelGesture
): Camera {
  if (gesture.zoom) {
    // A pinch reports much larger deltas than a wheel notch; damp them to the same feel.
    const scaled = gesture.deltaY * PINCH_DAMPING;
    return zoomAt(camera, viewport, anchorScreen, WHEEL_ZOOM_BASE ** -scaled);
  }

  /*
   * Shift makes a vertical wheel scroll sideways — the convention everywhere, and the only way
   * a plain mouse can pan horizontally at all. Browsers already move the delta into `deltaX`
   * for some devices, so this applies only when they have not.
   */
  const sideways = gesture.shift && gesture.deltaX === 0;
  const dx = sideways ? gesture.deltaY : gesture.deltaX;
  const dy = sideways ? 0 : gesture.deltaY;

  // Scrolling down moves the view down, which moves the content up the screen.
  return panByScreen(camera, -dx, -dy);
}

/** A discrete zoom step, for keyboard shortcuts and toolbar buttons. */
export function zoomStep(
  camera: Camera,
  viewport: Viewport,
  anchorScreen: Vec2,
  steps: number
): Camera {
  return zoomAt(camera, viewport, anchorScreen, 1.25 ** steps);
}

/** Resets zoom to 1:1 about the given anchor, preserving what is under it. */
export function resetZoom(camera: Camera, viewport: Viewport, anchorScreen: Vec2): Camera {
  return zoomAt(camera, viewport, anchorScreen, 1 / camera.zoom);
}
